// src/pages/Menu/Vinculation/CRM/CRMBoard.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { RefreshCcw, Search, TrendingUp, MapPin, Calendar, BarChart3, ArrowLeft, Filter, ChevronDown, ChevronUp, Plus, Tag, XCircle, Clock } from 'lucide-react';
import { Button } from '@mui/material';
import { Banner } from '../../../../components/common/Banner';
import TimelineFilter from '../../../../components/common/TimelineFilter';
import SectionTips from '../../../../components/common/SectionTips';
import TooltipHelper from '../../../../components/common/TooltipHelper';
import { crmListSummary, crmAnalytics, crmGetRecentMeetings } from '../../../../services/vinculationCRMService';
import CompanyCRMModal from './CompanyCRMModal';
import QuickCreateMeetingModal from './QuickCreateMeetingModal';
import { useAuth } from '../../../../context/authContext';
import { useNavigate } from 'react-router-dom';
import ArrowBack from '../../../../components/common/ArrowBack';

const normalizeCompany = (c) => ({
  rut: c.rut ?? c.rutEmpresa ?? '',
  name: c.trade_name?.trim() ? c.trade_name : (c.business_name || 'Sin nombre'),
  business_name: c.business_name ?? '',
  trade_name: c.trade_name ?? '',
  industry: c.industry ?? '',
  region: c.region ?? '',
  district: c.district ?? '',
  logo: c.logo ?? c.logo_url ?? null,
  // prioriza el conteo /año si el backend lo envía
  meetingsCount: Number(c.meetingsCountYear ?? c.meetingsCount ?? 0),
  lastMeetingAt: c.lastMeetingAt ?? null,
  role: (c.role ?? '').toString().toLowerCase(),
  entry_date: c.entry_date ?? null,
});

const parseDateToLocal = (dateString) => {
  if (!dateString) return null;
  const cleanDateString = dateString.split('T')[0];
  const parts = cleanDateString.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

const onlyDate = (iso) => {
  if (!iso) return '—';
  const d = parseDateToLocal(iso);
  if (!d || Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

// Helper para convertir HEX a RGBA (similar a indicadores financieros)
const hexToRgba = (hex = "#0488AF", alpha = 0.12) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return `rgba(11,74,143,${alpha})`;
  const r = parseInt(m[1], 16),
    g = parseInt(m[2], 16),
    b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// Colores mejorados similares a indicadores financieros
const linkTone = (n) => {
  if (n >= 10) {
    // Verde para alto vínculo (similar a #10B981 de indicadores)
    return {
      color: '#10B981',
      dot: 'bg-[#10B981]',
      text: 'text-[#10B981]',
      border: 'border-slate-200',
      ring: 'ring-slate-200',
      bgBadge: hexToRgba('#10B981', 0.12),
      borderBadge: hexToRgba('#10B981', 0.4),
    };
  }
  if (n >= 5) {
    // Ámbar para vínculo medio (similar a #F59E0B de indicadores)
    return {
      color: '#F59E0B',
      dot: 'bg-[#F59E0B]',
      text: 'text-[#F59E0B]',
      border: 'border-slate-200',
      ring: 'ring-slate-200',
      bgBadge: hexToRgba('#F59E0B', 0.12),
      borderBadge: hexToRgba('#F59E0B', 0.4),
    };
  }
  if (n === 0) {
    // Rojo para sin reuniones
    return {
      color: '#EF4444',
      dot: 'bg-[#EF4444]',
      text: 'text-[#EF4444]',
      border: 'border-slate-200',
      ring: 'ring-slate-200',
      bgBadge: hexToRgba('#EF4444', 0.12),
      borderBadge: hexToRgba('#EF4444', 0.4),
    };
  }
  // Rojo para vínculo bajo (similar a #EF4444 de indicadores)
  return {
    color: '#EF4444',
    dot: 'bg-[#EF4444]',
    text: 'text-[#EF4444]',
    border: 'border-slate-200',
    ring: 'ring-slate-200',
    bgBadge: hexToRgba('#EF4444', 0.12),
    borderBadge: hexToRgba('#EF4444', 0.4),
  };
};

const bucketOf = (n) => (n >= 10 ? 'high' : n >= 5 ? 'mid' : n === 0 ? 'zero' : 'low');

const Card = ({ item, onOpen, filterYear, yearFrom, monthFrom, yearTo, monthTo }) => {
  const tone = linkTone(item.meetingsCount);
  
  // Verificar si lastMeetingAt corresponde al filtro aplicado
  const shouldShowLastMeeting = item.lastMeetingAt && (() => {
    const d = parseDateToLocal(item.lastMeetingAt);
    if (!d) return false;
    
    // Si hay rango de fechas, verificar que esté dentro del rango
    if (yearFrom && monthFrom && yearTo && monthTo) {
      const meetingYear = d.getFullYear();
      const meetingMonth = d.getMonth() + 1; // getMonth() es 0-indexed
      
      const fromYear = Number(yearFrom);
      const fromMonth = Number(monthFrom);
      const toYear = Number(yearTo);
      const toMonth = Number(monthTo);
      
      // Verificar si la fecha está dentro del rango
      if (meetingYear < fromYear || meetingYear > toYear) return false;
      if (meetingYear === fromYear && meetingMonth < fromMonth) return false;
      if (meetingYear === toYear && meetingMonth > toMonth) return false;
      
      return true;
    }
    
    // Si solo hay año, verificar que coincida
    if (filterYear) {
      return d.getFullYear() === Number(filterYear);
    }
    
    // Si no hay filtros, mostrar siempre
    return true;
  })();
  
  return (
    <button
      onClick={() => onOpen(item)}
      className="group text-left rounded-2xl border border-gray-200 bg-white p-5 relative hover:shadow-lg hover:border-[#0488AF]/30 transition-all duration-200 w-full overflow-hidden"
      style={{ 
        borderLeft: `8px solid ${tone.color}`,
      }}
    >
      {/* Header con logo y badge */}
      <div className="flex items-start gap-4 mb-4">
        <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center overflow-hidden flex-shrink-0 shadow-md border-2 border-gray-100 group-hover:border-[#0488AF]/20 transition-colors">
          {item.logo ? (
            <img src={item.logo} alt="" className="w-full h-full object-contain p-2" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#0488AF] to-[#09274A] flex items-center justify-center text-white text-2xl font-bold">
              {(item.name || 'S').charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1 pt-1">
          <div className="font-bold text-lg text-[#0b3a66] truncate mb-1 group-hover:text-[#0488AF] transition-colors">
            {item.name}
          </div>
        </div>

        <div className="flex items-start shrink-0 pt-1">
          <div
            className="inline-flex flex-col items-center justify-center px-3 py-2 rounded-xl text-center min-w-[70px]"
            style={{
              color: tone.color,
              backgroundColor: tone.bgBadge,
              border: `1.5px solid ${tone.borderBadge}`,
            }}
          >
            <span className="text-lg font-bold leading-none">{item.meetingsCount}</span>
            <span className="text-[10px] font-medium uppercase leading-tight mt-0.5">
              {item.meetingsCount === 1 ? 'reunión' : 'reuniones'}
            </span>
          </div>
        </div>
      </div>

      {/* Información adicional */}
      <div className="space-y-2.5">
        {item.industry && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="p-1.5 rounded-lg bg-gray-50">
              <TrendingUp className="w-3.5 h-3.5 text-gray-500" />
            </div>
            <span className="truncate font-medium">{item.industry}</span>
          </div>
        )}
        
        {(item.region || item.district) && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="p-1.5 rounded-lg bg-gray-50">
              <MapPin className="w-3.5 h-3.5 text-gray-500" />
            </div>
            <span className="truncate">
              {item.region || '—'}{item.district ? ` / ${item.district}` : ''}
            </span>
          </div>
        )}

        {shouldShowLastMeeting && (
          <div className="flex items-center gap-2 text-sm text-gray-600 pt-2 border-t border-gray-100">
            <div className="p-1.5 rounded-lg bg-gray-50">
              <Calendar className="w-3.5 h-3.5 text-gray-500" />
            </div>
            <span className="text-xs text-gray-500">Última reunión:</span>
            <span className="font-semibold text-gray-700">{onlyDate(item.lastMeetingAt)}</span>
          </div>
        )}
      </div>
    </button>
  );
};

const StatBtn = ({ label, value, tone, active, onClick, unit = 'reuniones' }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group w-full min-h-[140px] text-left rounded-2xl p-6 border-2 transition-all duration-200 relative overflow-hidden flex flex-col justify-between ${
      active 
        ? 'bg-[#0488AF]/10 border-[#0488AF] shadow-md shadow-[#0488AF]/10' 
        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
    }`}
  >
    {active && (
      <div className="absolute inset-0 bg-gradient-to-br from-[#0488AF]/5 to-transparent" />
    )}

    <div className="relative flex-1 flex flex-col justify-between">
      <div className={`text-sm font-medium mb-3 ${
        active ? 'text-[#0488AF]' : 'text-gray-600'
      }`}>
        {label}
      </div>
      <div className={`text-4xl font-bold leading-none ${tone} ${
        active ? 'drop-shadow-sm' : ''
      }`}>
        {value} <span className="text-lg font-normal">{unit}</span>
      </div>
    </div>

    {active && (
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#0488AF] rounded-b-xl" />
    )}
  </button>
);

export default function CRMBoard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const myRutRaw = (user?.rut || localStorage.getItem('rut') || '').toString();
  const myRut = myRutRaw.replace(/\./g, '').replace(/-/g, '').trim();
  const myBusinessName = (user?.business_name || '').toString().trim().toLowerCase();
  const myTradeName = (user?.trade_name || '').toString().trim().toLowerCase();
  const isAdmin = user?.role === 'admin';

  const [raw, setRaw] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]); // Todas las empresas sin filtrar por reuniones
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [industry, setIndustry] = useState('');
  const [error, setError] = useState('');
  const [rel, setRel] = useState('all'); // all | high | mid | low | zero

  // Año seleccionado y opciones dinámicas
  const [year, setYear] = useState('');      // '', '2025', etc.
  const [yearOptions, setYearOptions] = useState(['']); // se llena con discoverYears() 
  const discoveringRef = useRef(false);

  // Mes seleccionado (1-12, solo activo cuando hay año)
  const [month, setMonth] = useState('');     // '', '1', '2', ..., '12'

  // Rango de fechas (desde/hasta)
  const [yearFrom, setYearFrom] = useState('');
  const [monthFrom, setMonthFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [monthTo, setMonthTo] = useState('');

  // Filtros de fecha desplegables
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [initialMeetingId, setInitialMeetingId] = useState(null);
  const [openQuickCreate, setOpenQuickCreate] = useState(false);
  const [recentMeetings, setRecentMeetings] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  const openModal = (item, meetingId = null) => { 
    setSelected(item); 
    setInitialMeetingId(meetingId);
    setOpen(true); 
  };
  const closeModal = () => { 
    setOpen(false); 
    setSelected(null); 
    setInitialMeetingId(null);
  };
  
  // Función para abrir reunión desde el banner
  const openMeetingFromBanner = (meeting) => {
    const rut = meeting.rut || meeting.companyRut || meeting.company?.rut;
    if (!rut) return;
    
    // Buscar la empresa en la lista
    const normalizedRut = String(rut).replace(/\./g, '').replace(/-/g, '').trim();
    const company = raw.find(c => {
      const companyRut = String(c.rut || '').replace(/\./g, '').replace(/-/g, '').trim();
      return companyRut === normalizedRut;
    });
    
    if (company) {
      openModal(company, meeting.id);
    }
  };

  // Cargar todas las empresas (sin filtrar por reuniones) para estadísticas
  const loadAllCompanies = async () => {
    try {
      const list = await crmListSummary(undefined); // Sin filtros
      let norm = (Array.isArray(list) ? list : []).map(normalizeCompany);

      // excluir admins/propia empresa
      norm = norm.filter((c) => {
        const rutNorm = (c.rut || '').toString().replace(/\./g, '').replace(/-/g, '').trim();
        const isMine = !!myRut && rutNorm === myRut;
        const isMyBusinessName = isAdmin && myBusinessName && (c.business_name || '').toString().trim().toLowerCase() === myBusinessName;
        const isMyTradeName = isAdmin && myTradeName && (c.trade_name || c.name || '').toString().trim().toLowerCase() === myTradeName;
        const looksOwner = (c.trade_name || c.name || '').toString().toLowerCase().includes('cidere');
        return c.role !== 'admin' && !isMine && !isMyBusinessName && !isMyTradeName && !looksOwner;
      });

      setAllCompanies(norm);
    } catch (e) {
      setAllCompanies([]);
    }
  };

  // Cargar cards según año y mes
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      // Si hay rango de fechas (desde/hasta), usar rango
      if (yearFrom && monthFrom && yearTo && monthTo) {
        params.yearFrom = Number(yearFrom);
        params.monthFrom = Number(monthFrom);
        params.yearTo = Number(yearTo);
        params.monthTo = Number(monthTo);
      } else if (year) {
        // Si hay año individual, usar filtro de año/mes
        params.year = Number(year);
        if (month && year) {
          // Si month tiene formato "1-9" (rango), parsearlo
          const monthParts = month.split('-');
          if (monthParts.length === 2) {
            // Rango de meses: "1-9" significa enero a septiembre
            params.monthFrom = Number(monthParts[0]);
            params.monthTo = Number(monthParts[1]);
          } else {
            // Mes individual
            params.month = Number(month);
          }
        }
      }
      
      const list = await crmListSummary(Object.keys(params).length ? params : undefined);
      const hasRange = yearFrom && monthFrom && yearTo && monthTo;
      let norm = (Array.isArray(list) ? list : []).map(c => {
        const normalized = normalizeCompany(c);
        // Si hay un rango o año seleccionado, usar meetingsCountYear
        // Si no hay año ni rango, usar meetingsCount (total histórico)
        if (hasRange || year) {
          return {
            ...normalized,
            meetingsCount: Number(c.meetingsCountYear ?? 0)
          };
        } else {
          // Sin año ni rango seleccionado, usar meetingsCount (total histórico)
          return {
            ...normalized,
            meetingsCount: Number(c.meetingsCount ?? 0)
          };
        }
      });

      // excluir admins/propia empresa
      norm = norm.filter((c) => {
        const rutNorm = (c.rut || '').toString().replace(/\./g, '').replace(/-/g, '').trim();
        const isMine = !!myRut && rutNorm === myRut;
        const isMyBusinessName = isAdmin && myBusinessName && (c.business_name || '').toString().trim().toLowerCase() === myBusinessName;
        const isMyTradeName = isAdmin && myTradeName && (c.trade_name || c.name || '').toString().trim().toLowerCase() === myTradeName;
        const looksOwner = (c.trade_name || c.name || '').toString().toLowerCase().includes('cidere');
        return c.role !== 'admin' && !isMine && !isMyBusinessName && !isMyTradeName && !looksOwner;
      });

      setRaw(norm);
    } catch (e) {
      setRaw([]);
      setError(e?.message || 'No se pudo cargar.');
    } finally {
      setLoading(false);
    }
  };

  // Cargar reuniones recientes
  const loadRecentMeetings = useCallback(async () => {
    setLoadingRecent(true);
    try {
      // Aumentar el límite a 10 para asegurar que se muestren todas las reuniones recientes
      const meetings = await crmGetRecentMeetings(10);
      const meetingsArray = Array.isArray(meetings) ? meetings : [];
      setRecentMeetings(meetingsArray);
    } catch (e) {
      console.error('Error al cargar reuniones recientes:', e);
      setRecentMeetings([]);
    } finally {
      setLoadingRecent(false);
    }
  }, []);

  // Descubre años con actividad usando analytics en un rango amplio (pasado y futuro cercano)
  const discoverYears = async () => {
    if (discoveringRef.current) return;
    discoveringRef.current = true;

    const now = new Date().getFullYear();
    const minYear = now - 12;
    const maxYear = now + 3; // incluye futuros como 2027 si hay datos
    const candidates = [];
    for (let y = minYear; y <= maxYear; y++) candidates.push(y);

    try {
      const results = await Promise.allSettled(
        candidates.map((y) => crmAnalytics({ year: y }))
      );

      const yearsWithData = candidates.filter((y, i) => {
        const r = results[i];
        if (r.status !== 'fulfilled') return false;
        const meetings = Number(r.value?.totals?.meetings ?? 0);
        return meetings > 0;
        // si quieres incluir años con 0 para que aparezcan igual, cambia la condición a >= 0
      });

      // asegúrate de incluir siempre el año actual y el más alto con datos
      const latestWithData = yearsWithData.length ? Math.max(...yearsWithData) : now;
      const opts = Array.from(new Set(['', ...yearsWithData, now]))
        .filter((x) => x === '' || Number.isFinite(x))
        .sort((a, b) => (a === '' ? 1 : b === '' ? -1 : b - a))
        .map(String);

      setYearOptions(opts);

      // si no hay año seleccionado, setea el más alto con datos (ej: 2027) o el actual
      if (!year) setYear(String(latestWithData));
    } catch {
      // Fallback: deriva de las fechas vistas en raw + asegúrate de incluir el año actual
      const ys = new Set(
        raw
          .map((x) => {
            if (!x.lastMeetingAt) return null;
            const d = parseDateToLocal(x.lastMeetingAt);
            return d ? d.getFullYear() : null;
          })
          .filter(Boolean)
      );
      ys.add(new Date().getFullYear());
      const opts = [''].concat([...ys].sort((a, b) => b - a)).map(String);
      setYearOptions(opts);
      if (!year) setYear(String(Math.max(...[...ys])));
    } finally {
      discoveringRef.current = false;
    }
  };

  // Efectos
  useEffect(() => { loadAllCompanies(); }, [myRut]); // Cargar todas las empresas una vez
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [myRut, year, month, yearFrom, monthFrom, yearTo, monthTo]);
  useEffect(() => { loadRecentMeetings(); }, [loadRecentMeetings]); // Cargar reuniones recientes al montar
  
  // Escuchar eventos globales para actualizar reuniones recientes
  useEffect(() => {
    const handleMeetingCreated = () => {
      // Agregar un pequeño delay para asegurar que el backend haya procesado la nueva reunión
      setTimeout(() => {
        loadRecentMeetings();
      }, 500);
    };
    window.addEventListener('meetingCreated', handleMeetingCreated);
    window.addEventListener('meetingUpdated', handleMeetingCreated);
    window.addEventListener('meetingDeleted', handleMeetingCreated);
    return () => {
      window.removeEventListener('meetingCreated', handleMeetingCreated);
      window.removeEventListener('meetingUpdated', handleMeetingCreated);
      window.removeEventListener('meetingDeleted', handleMeetingCreated);
    };
  }, [loadRecentMeetings]);
  useEffect(() => { discoverYears(); /* eslint-disable-next-line */ }, []);          // al montar
  useEffect(() => { if (!discoveringRef.current) discoverYears(); /* eslint-disable-next-line */ }, [raw.length]); // tras cargar/insertar
  
  // Resetear mes cuando cambia el año
  useEffect(() => { setMonth(''); }, [year]);

  const industryOptions = useMemo(() => {
    const set = new Set(raw.map((x) => (x.industry || '').trim()).filter(Boolean));
    return [''].concat([...set].sort((a, b) => a.localeCompare(b, 'es')));
  }, [raw]);


  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return raw.filter((c) => {
      const matchText =
        !ql ||
        c.name.toLowerCase().includes(ql) ||
        c.business_name.toLowerCase().includes(ql) ||
        c.rut.toLowerCase().includes(ql) ||
        c.region.toLowerCase().includes(ql) ||
        c.district.toLowerCase().includes(ql);
      const matchInd = !industry || c.industry === industry;
      const matchRel = rel === 'all' || bucketOf(c.meetingsCount) === rel;
      return matchText && matchInd && matchRel;
    });
  }, [raw, q, industry, rel]);

  const statBase = useMemo(() => {
    // Total: cantidad acumulativa de empresas según fecha de ingreso
    // Si hay un año seleccionado, contar todas las empresas que se unieron hasta ese año (acumulativo)
    let total = raw.length;
    
    if (year && !yearFrom) {
      // Contar empresas acumulativamente hasta el año seleccionado
      const selectedYear = Number(year);
      total = allCompanies.filter((c) => {
        if (!c.entry_date) {
          // Si no tiene fecha de ingreso, incluirla (empresas antiguas sin fecha)
          return true;
        }
        const d = parseDateToLocal(c.entry_date);
        if (!d) return true; // Incluir si no se puede parsear
        const entryYear = d.getFullYear();
        // Incluir todas las empresas que se unieron hasta el año seleccionado (acumulativo)
        return entryYear <= selectedYear;
      }).length;
    } else if (yearFrom && monthFrom && yearTo && monthTo) {
      // Si hay un rango de fechas, contar empresas que se unieron hasta el final del rango (acumulativo)
      const toYear = Number(yearTo);
      const toMonth = Number(monthTo);
      total = allCompanies.filter((c) => {
        if (!c.entry_date) {
          return true;
        }
        const d = parseDateToLocal(c.entry_date);
        if (!d) return true;
        const entryYear = d.getFullYear();
        const entryMonth = d.getMonth() + 1;
        // Incluir todas las empresas que se unieron hasta el final del rango (acumulativo)
        if (entryYear < toYear) return true;
        if (entryYear === toYear && entryMonth <= toMonth) return true;
        return false;
      }).length;
    } else {
      // Si no hay filtros, usar el total de todas las empresas
      total = allCompanies.length || raw.length;
    }
    
    // Para calcular los buckets (high, mid, low, zero), usar todas las empresas de raw
    // raw ya tiene meetingsCount con el valor correcto (meetingsCountYear si hay año, meetingsCount si no)
    const high = raw.filter((x) => bucketOf(x.meetingsCount) === 'high').length;
    const mid  = raw.filter((x) => bucketOf(x.meetingsCount) === 'mid').length;
    const low  = raw.filter((x) => bucketOf(x.meetingsCount) === 'low').length;
    const zero = raw.filter((x) => bucketOf(x.meetingsCount) === 'zero').length;
    return { total, high, mid, low, zero };
  }, [raw, allCompanies, year, yearFrom, monthFrom, yearTo, monthTo]);

  // Nombres de meses para etiquetas
  const monthNames = {
    '1': 'Enero', '2': 'Febrero', '3': 'Marzo', '4': 'Abril',
    '5': 'Mayo', '6': 'Junio', '7': 'Julio', '8': 'Agosto',
    '9': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
  };

  const hasRange = yearFrom && monthFrom && yearTo && monthTo;
  const hasMonthRange = month && month.includes('-');
  let monthLabel = '';
  let yearLabel = '';
  
  if (hasRange) {
    yearLabel = ` /rango ${monthNames[monthFrom] || monthFrom} ${yearFrom} - ${monthNames[monthTo] || monthTo} ${yearTo}`;
  } else if (year) {
    yearLabel = ` /año ${year}`;
    if (month) {
      if (hasMonthRange) {
        const [fromMonth, toMonth] = month.split('-');
        monthLabel = ` /mes ${monthNames[fromMonth] || fromMonth} - ${monthNames[toMonth] || toMonth}`;
      } else {
        monthLabel = ` /mes ${monthNames[month] || month}`;
      }
    }
  } else {
    yearLabel = ' /año';
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-6 space-y-6">
      <div className="relative">
        <div className="px-1 pb-2">
          <ArrowBack />
        </div>
        <Banner title="Relacionamiento con socios" message="N° de reuniones y minutas" />
      </div>

      <SectionTips
        section="el relacionamiento con socios"
        buttonTitle="¿Cómo usar esta sección?"
        tipsTitle="¿Cómo usar esta sección? Relacionamiento con socios"
        tipsList={[
          'En está sección puedes gestionar el relacionamiento de CIDERE con todos sus socios.',
          'Las tarjetas del inferior de la página corresponden a los socios, te muestran un pequeño resumen de su información, un contador de la cantidad de reuniones y la fecha de la última reunión.',
          'Al entrar a la sección, por defecto se muestran los datos correspondientes al año actual, al presionar <strong>"Filtro de fecha"</strong> puedes filtrar por cualquier rango de fecha o presionar <strong>"limpiar"</strong> para mostrar los totales.',
          'Dentro de la tarjeta de un socio podrás ver y registrar reuniones con información detallada como descripción, categoría, participantes y adjuntar un archivo de minuta, también puedes ver un resumen de indicadores sobre las reuniones que han tenido y finalmente puedes ver y registrar notas con información relevante que sea transversal a las reuniones.',
          'Los recuadros arriba de los socios son contadores y a su vez filtros, al presionarlos puedes alternar entre ver todos los socios, los con <strong>alto</strong>, <strong>medio</strong>, <strong>bajo vínculo</strong> y <strong>sin reuniones</strong>.',
          'El botón de <strong>"Ver indicadores"</strong> te llevará a una página donde puedes ver los datos históricos.',
        ]}
        finalNote=""
        color="#0488AF"
        videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766417819/Video_Tutorial_m%C3%B3dulo_Vinculaci%C3%B3n_Empresarial_-_Relacionamiento_con_Socios_dntx0m.mp4"
        manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412955/Manual_de_usuario_m%C3%B3dulo_de_vinculaci%C3%B3n_empresarial_af2jv3.pdf"
      />

      {/* Card destacado para crear reunión */}
      <div className="rounded-2xl overflow-hidden shadow-lg border-2 border-[#0488AF]/20 bg-gradient-to-br from-[#0488AF]/5 via-white to-[#0488AF]/5">
        <div className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0488AF] to-[#037a98] flex items-center justify-center shadow-md">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">Gestiona tus reuniones</h3>
                  <p className="text-sm md:text-base text-gray-600 mt-1">
                    Crea y registra reuniones con empresas socias de forma rápida y sencilla
                  </p>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpenQuickCreate(true)}
              className="group relative inline-flex items-center justify-center gap-3 px-6 md:px-8 py-3 md:py-3.5 rounded-xl bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm md:text-base font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 min-w-[200px] md:min-w-[230px]"
              title="Crear nueva reunión rápidamente"
            >
              <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
              <span>Agregar nueva reunión</span>
            </button>
          </div>
          {/* Botones pequeños abajo */}
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            <Button
              variant="contained"
              size="small"
              startIcon={<BarChart3 size={18} />}
              sx={{
                background: 'linear-gradient(to right, #09274A, #0488AF)',
                color: '#fff',
                textTransform: 'none',
                minWidth: 140,
                minHeight: '40px',
                padding: '6px 16px',
                lineHeight: '1.5',
                '&:hover': {
                  background: 'linear-gradient(to right, #071f3d, #03779C)',
                },
              }}
              title="Ver indicadores"
              onClick={() => navigate('/panel/vinculacion-empresarial/Indicators')}
            >
              Ver indicadores
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshCcw size={18} />}
              sx={{
                background: 'linear-gradient(to right, #09274A, #0488AF)',
                color: '#fff',
                textTransform: 'none',
                minWidth: 140,
                minHeight: '40px',
                padding: '6px 16px',
                lineHeight: '1.5',
                '&:hover': {
                  background: 'linear-gradient(to right, #071f3d, #03779C)',
                },
              }}
              title="Refrescar"
              onClick={load}
            >
              Refrescar
            </Button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {/* Primera fila: Búsqueda y Rubro */}
        <div className="p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_180px]">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-[#09274A] whitespace-nowrap tracking-wide uppercase">
                Buscar
              </span>
              <div className="relative flex items-center gap-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Razón social, RUT, región, comuna…"
                  className="w-full h-11 pl-10 pr-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A] placeholder:text-gray-400"
                />
                <TooltipHelper text="Busca empresas socias por razón social, RUT, región o comuna. La búsqueda filtra en tiempo real." />
              </div>
            </div>

            <div className="space-y-1">
              <span className="text-xs font-semibold text-[#09274A] whitespace-nowrap tracking-wide uppercase">
                Rubro
              </span>
              <div className="flex items-center gap-2">
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="h-11 px-3 rounded-2xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#0488AF] outline-none text-sm text-[#09274A]"
                  title="Rubro"
                >
                  {industryOptions.map((opt, i) => (
                    <option key={i} value={opt}>
                      {opt || 'Todos los rubros'}
                    </option>
                  ))}
                </select>
                <TooltipHelper text="Filtra las empresas por su rubro o industria. Selecciona un rubro específico o 'Todos los rubros' para ver todas." />
              </div>
            </div>
          </div>

        </div>

        {/* Filtros de fecha desplegables */}
        <div 
          className="border-t border-gray-200"
          style={{
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)"
          }}
        >
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/50 transition-colors"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)"
            }}
          >
            <div className="flex items-center gap-1.5">
              <Filter size={16} className="text-[#0488AF]" />
              <span className="text-xs font-semibold text-[#09274A] tracking-wide uppercase">
                Filtros de fecha
              </span>
              <TooltipHelper text="Filtra las reuniones por año y/o rango de fechas. Seguirán apareciendo las tarjetas de todos los socios, pero el conteo de reuniones y los niveles de vínculo se ajustarán según el periodo seleccionado. Usa 'Limpiar' para ver todos los datos sin filtros." />
            </div>
            {filtersExpanded ? (
              <ChevronUp size={16} className="text-[#0488AF]" />
            ) : (
              <ChevronDown size={16} className="text-[#0488AF]" />
            )}
          </button>

          {filtersExpanded && (
            <div 
              className="p-2 border-t border-gray-200/50"
              style={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
              }}
            >
              <TimelineFilter
                year={year}
                month={month}
                yearOptions={yearOptions}
                onYearChange={setYear}
                onMonthChange={setMonth}
                yearFrom={yearFrom}
                monthFrom={monthFrom}
                yearTo={yearTo}
                monthTo={monthTo}
                onYearFromChange={setYearFrom}
                onMonthFromChange={setMonthFrom}
                onYearToChange={setYearTo}
                onMonthToChange={setMonthTo}
              />
            </div>
          )}
        </div>
      </div>

      {/* Indicador de filtros activos - siempre visible */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-5 h-5 text-blue-600 shrink-0" />
          <span className="text-sm font-semibold text-blue-900">Filtro de fecha aplicado:</span>
          {yearFrom && monthFrom && yearTo && monthTo ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
              Rango: {monthFrom}/{yearFrom} - {monthTo}/{yearTo}
            </span>
          ) : year ? (
            <>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
                Año: {year}
              </span>
              {month && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-semibold">
                  Mes: {month === '1' ? 'Enero' : month === '2' ? 'Febrero' : month === '3' ? 'Marzo' : month === '4' ? 'Abril' : month === '5' ? 'Mayo' : month === '6' ? 'Junio' : month === '7' ? 'Julio' : month === '8' ? 'Agosto' : month === '9' ? 'Septiembre' : month === '10' ? 'Octubre' : month === '11' ? 'Noviembre' : 'Diciembre'}
                </span>
              )}
            </>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium">
              Sin filtros (mostrando todos los años)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const currentYear = String(new Date().getFullYear());
              setYear(currentYear);
              setMonth('');
              setYearFrom('');
              setMonthFrom('');
              setYearTo('');
              setMonthTo('');
            }}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-xs font-semibold tracking-wide uppercase shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors whitespace-nowrap"
            title="Filtrar solo por el año actual y limpiar otros filtros de fecha"
          >
            <Calendar size={16} />
            Filtrar año actual
          </button>
          <button
            onClick={() => {
              setYear('');
              setMonth('');
              setYearFrom('');
              setMonthFrom('');
              setYearTo('');
              setMonthTo('');
              setQ('');
              setIndustry('');
              setRel('all');
            }}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-[#DC3465] text-[#DC3465] text-xs font-semibold tracking-wide uppercase hover:bg-[#C42251] hover:text-white transition-colors whitespace-nowrap"
            title="Limpiar todos los filtros para ver todos los datos"
          >
            <XCircle size={16} />
            Limpiar todas
          </button>
        </div>
      </div>

      {/* Indicadores rápidos /año */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-stretch">
        <StatBtn
          label={`Todas las empresas${yearLabel}${monthLabel}`}
          value={statBase.total}
          tone="text-[#0488AF]"
          active={rel === 'all'}
          onClick={() => setRel('all')}
          unit="empresas"
        />
        <StatBtn
          label={`Alto vínculo${yearLabel}${monthLabel} (10+ reuniones)`}
          value={statBase.high}
          tone="text-green-700"
          active={rel === 'high'}
          onClick={() => setRel('high')}
        />
        <StatBtn
          label={`Vínculo medio${yearLabel}${monthLabel} (5-9 reuniones)`}
          value={statBase.mid}
          tone="text-amber-700"
          active={rel === 'mid'}
          onClick={() => setRel('mid')}
        />
        <StatBtn
          label={`Vínculo bajo${yearLabel}${monthLabel} (1-4 reuniones)`}
          value={statBase.low}
          tone="text-rose-700"
          active={rel === 'low'}
          onClick={() => setRel('low')}
        />
        <StatBtn
          label={`Sin reuniones${yearLabel}${monthLabel} (0 reuniones)`}
          value={statBase.zero}
          tone="text-gray-700"
          active={rel === 'zero'}
          onClick={() => setRel('zero')}
        />
        <TooltipHelper text="Estos botones filtran las empresas según su nivel de vínculo (cantidad de reuniones). Todas: muestra todas las empresas. Alto: 10+ reuniones, Medio: 5-9 reuniones, Bajo: 1-4 reuniones, Sin reuniones: 0 reuniones. El conteo depende del filtro de fecha seleccionado." />
      </div>

      {/* Banner de últimas 3 reuniones */}
      {recentMeetings && recentMeetings.length > 0 && (
        <div className="rounded-2xl overflow-hidden shadow-lg border-2 border-[#0488AF]/20 bg-gradient-to-br from-[#0488AF]/5 via-white to-[#0488AF]/5">
          <div className="p-4 md:p-5">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0488AF] to-[#037a98] flex items-center justify-center shadow-md">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900">Últimas 3 reuniones</h3>
                    <p className="text-xs md:text-sm text-gray-600 mt-0.5">
                      Reuniones más recientes que has registrado
                    </p>
                  </div>
                </div>
                {/* Lista de reuniones */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  {loadingRecent ? (
                    <div className="col-span-3 text-xs text-gray-500 py-1">Cargando...</div>
                  ) : (
                    recentMeetings.slice(0, 3).map((meeting, idx) => {
                      const companyName = meeting.companyName || meeting.company?.name || meeting.company?.trade_name || meeting.company?.business_name || 'Sin nombre';
                      return (
                        <button
                          key={meeting.id || idx}
                          onClick={() => openMeetingFromBanner(meeting)}
                          className="group text-left flex items-center gap-3 px-4 py-3 rounded-xl bg-white border-2 border-gray-200 hover:border-[#0488AF] hover:shadow-md transition-all cursor-pointer"
                        >
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#0488AF] to-[#037a98] flex items-center justify-center shadow-sm">
                            <span className="text-sm font-bold text-white">{idx + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-gray-900 truncate group-hover:text-[#0488AF] transition-colors">
                              {companyName}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                              <Calendar className="w-3 h-3 text-gray-500 flex-shrink-0" />
                              <span className="text-xs text-gray-600 font-medium">
                                {onlyDate(meeting.date)}
                              </span>
                            </div>
                            {meeting.title && (
                              <div className="text-xs text-gray-500 truncate mt-1">
                                {meeting.title}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner de tarjetas de socios */}
      {!loading && !error && filtered.length > 0 && (
        <div className="rounded-2xl overflow-hidden shadow-lg border-2 border-[#0488AF]/20 bg-gradient-to-br from-[#0488AF]/5 via-white to-[#0488AF]/5">
          <div className="p-4 md:p-5">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0488AF] to-[#037a98] flex items-center justify-center shadow-md">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900">Socios</h3>
                  <p className="text-xs md:text-sm text-gray-600 mt-0.5">
                    Listado de empresas socias
                  </p>
                </div>
              </div>
            </div>
            {/* Grid de tarjetas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((item) => (
                <Card 
                  key={`${item.rut}-${item.name}`} 
                  item={item} 
                  onOpen={openModal} 
                  filterYear={year}
                  yearFrom={yearFrom}
                  monthFrom={monthFrom}
                  yearTo={yearTo}
                  monthTo={monthTo}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Estados de carga y error */}
      {loading && (
        <div className="py-14 text-center text-gray-500">Cargando…</div>
      )}
      {error && (
        <div className="py-10 text-center text-rose-700">{error}</div>
      )}
      {!loading && !error && filtered.length === 0 && (
        <div className="py-14 text-center text-gray-500">No hay resultados.</div>
      )}

      {/* Modal detalle por empresa */}
      <CompanyCRMModal 
        open={open} 
        onClose={closeModal} 
        companyStub={selected}
        initialYear={year}
        initialMonth={month}
        initialYearFrom={yearFrom}
        initialMonthFrom={monthFrom}
        initialYearTo={yearTo}
        initialMonthTo={monthTo}
        initialMeetingId={initialMeetingId}
        onClearInitialMeetingId={() => setInitialMeetingId(null)}
        onMeetingCreated={() => {
          // Recargar la lista para actualizar el contador de reuniones
          load();
          loadRecentMeetings();
        }}
        onMeetingUpdated={() => {
          // Recargar la lista para actualizar el contador de reuniones
          load();
          loadRecentMeetings();
        }}
        onMeetingDeleted={() => {
          // Recargar la lista para actualizar el contador de reuniones
          load();
          loadRecentMeetings();
        }}
      />

      {/* Modal de creación rápida de reunión */}
      <QuickCreateMeetingModal
        open={openQuickCreate}
        onClose={() => setOpenQuickCreate(false)}
        onCreated={() => {
          // Recargar la lista para actualizar el contador de reuniones
          load();
          loadRecentMeetings();
        }}
      />
    </div>
  );
}
